import React from "react";

import moment from "moment";

import {
  formatCO2AmountRange,
  formatDate,
  formatNumericCurrency,
  isFeatureActiveOnCurrentFarm,
  isSingleFieldOutcomes,
  useGetCarbonProgramByFarmId,
  useGetDataSubmissionDates,
  useGetFarmPayments,
  useGetFarmReportingYear,
  useInventoryImpact,
  useTranslation,
} from "utils";

import { CURRENCY_CODE, FEATURE_FLAG_SLUGS } from "consts";

import { CarbonProgramOutcomesResponse, SingleFieldOutcomes } from "models";

import { Button, Flex, FlexPageSpin, Modal, Typography } from "components";

import { formatPayoutsPostSubmission } from "pages/app/all-field-view/utils/AllFieldsUtils";

type SharedSubmissionProps = {
  carbonProgramOutcomes: CarbonProgramOutcomesResponse | null;
  farmId: string;
};

interface SubmissionSuccessModalProps extends SharedSubmissionProps {
  visible: boolean;
  onClose: () => void;
}

interface ModalContentProps extends SharedSubmissionProps {
  onClose?: () => void;
  variant?: "modal" | "panel";
}

export const SubmissionSuccessModalContent = ({ carbonProgramOutcomes, farmId, onClose, variant = "modal" }: ModalContentProps) => {
  const { t } = useTranslation(["allFieldView"]);
  const { data: carbonProgram } = useGetCarbonProgramByFarmId(farmId ?? undefined);
  const {
    data: { activeFarmReportingYear },
    isLoading: isFarmReportingYearLoading,
  } = useGetFarmReportingYear(farmId ?? undefined);
  const { data: submissionDates } = useGetDataSubmissionDates({ farmId });
  const { data: farmPayments } = useGetFarmPayments(true);

  const isSoilPlus3DataGapFeatureActive = isFeatureActiveOnCurrentFarm(FEATURE_FLAG_SLUGS.SOIL_PLUS_3_DATA_GAP);
  const isSoilPlus3RewardsFeatureActive = isFeatureActiveOnCurrentFarm(FEATURE_FLAG_SLUGS.SOIL_PLUS_3_REWARDS);

  const targetYear = submissionDates?.submissionStartDate ? moment(submissionDates.submissionStartDate).year() : activeFarmReportingYear ?? moment().year();
  const submissionEndDate = submissionDates?.submissionEndDate ? formatDate(submissionDates.submissionEndDate, "LL", moment.ISO_8601) : null;

  const singleFieldOutcomes: SingleFieldOutcomes[] = Object.values(carbonProgramOutcomes ?? {}).filter(isSingleFieldOutcomes);
  const { totalPayoutMin: inventoryPayoutMin, totalPayoutMax: inventoryPayoutMax } = useInventoryImpact(
    targetYear,
    singleFieldOutcomes,
    farmId,
    carbonProgram ?? undefined
  );

  const submissionSuccessModalContent = formatPayoutsPostSubmission({
    carbonProgramOutcomes,
    carbonProgram: carbonProgram ?? null,
    targetYear,
    inventoryPayoutMin,
    inventoryPayoutMax,
    isSoilPlus3DataGapFeatureActive,
    isSoilPlus3RewardsFeatureActive,
  });

  const {
    totalPayoutMin: targetYearTotalPayoutMin,
    totalCO2Min: targetYearTotalCO2Min,
    totalCO2Max: targetYearTotalCO2Max,
  } = submissionSuccessModalContent ?? {};

  const targetYearPayoutFromWallet = farmPayments?.payments.find((payment) => payment.year === targetYear)?.amount;

  const totalPayoutMinToShow = Math.max(targetYearPayoutFromWallet ?? targetYearTotalPayoutMin ?? 0, 0);

  const isModalVariant = variant === "modal";
  const currency = carbonProgram?.currency ?? CURRENCY_CODE.EUR;

  const showCO2SectionValues = false; // 🚨 TODO : remove this once the CO2 section issue is fixed

  if (isFarmReportingYearLoading) {
    return <FlexPageSpin />;
  }

  return (
    <Flex
      vertical
      align="center"
      gap={isModalVariant ? "1.5rem" : "1rem"}
      className={`${isModalVariant ? "padding-400" : "padding-top-400"} submission-success-modal-content`}
    >
      <Flex align="center" justify="center">
        <Typography variant="paragraph-m" style={{ fontSize: isModalVariant ? "4rem" : "3rem" }}>
          🎉
        </Typography>
      </Flex>
      <Typography variant="h1" style={{ fontSize: isModalVariant ? undefined : "1.5rem" }} className="margin-0 centered-text">
        {t("submission.complete.title")}
      </Typography>
      <Flex vertical align="center" className="centered-text" style={{ margin: isModalVariant ? undefined : "0 auto 1rem" }}>
        <Typography variant="paragraph-l" className="margin-0">
          {t("submission.complete.reportingYearSuccessfullySubmitted", { reportingYear: targetYear })}
        </Typography>
      </Flex>
      <Flex
        gap="0.625rem"
        style={{ width: "100%" }}
        vertical={!isModalVariant} // Stack vertically in panel mode
      >
        <div className="submission-outcome-card-container">
          <div className="submission-outcome-card">
            <Typography variant="paragraph-m" className="margin-0 margin-bottom-50 text-center outcome-card-title">
              {formatNumericCurrency(totalPayoutMinToShow, { currency, decimals: 0 })}
            </Typography>
            <Typography variant="paragraph-m" className="margin-0 centered-text">
              {t("submission.complete.totalExpectedPayout")}
            </Typography>
          </div>
        </div>

        {showCO2SectionValues && (
          <div className="submission-outcome-card-container">
            <div className="submission-outcome-card">
              <Typography variant="paragraph-m" className={`${isModalVariant ? "margin-0" : "margin-bottom-50"} text-center outcome-card-title`}>
                {formatCO2AmountRange(targetYearTotalCO2Min ?? 0, targetYearTotalCO2Max ?? 0)} CO<sub>2</sub>e
              </Typography>
              <Typography variant="paragraph-m" className="margin-0">
                {t("submission.complete.sequestratedInOurSoil")}
              </Typography>
            </div>
          </div>
        )}
      </Flex>
      <Flex vertical align="center" style={{ width: "100%" }}>
        <Typography variant="paragraph-xl" className="margin-0 margin-bottom-50">
          {t("submission.complete.nextSteps")}
        </Typography>
        <Typography variant="paragraph-m" className="margin-0 next-steps-text">
          {t("submission.complete.verificationReviewDescription", { reportingYear: targetYear })}
        </Typography>
        {submissionEndDate && (
          <Typography variant="paragraph-m" className="margin-0 next-steps-text">
            {t("submission.complete.nextDataEntryDescription", { nextReportingYear: targetYear + 1, submissionEndDate })}
          </Typography>
        )}
      </Flex>
      {onClose && (
        <Button type="primary" size="middle" onClick={onClose} style={{ maxWidth: "23rem" }}>
          {t("submission.complete.done")}
        </Button>
      )}
    </Flex>
  );
};

export const SubmissionSuccessModal = ({ visible, onClose, carbonProgramOutcomes, farmId }: SubmissionSuccessModalProps) => (
  <Modal
    showModal={visible}
    footer={null}
    centered
    maskClosable
    onCancel={onClose}
    dataTestId="submission-success-modal"
    className="submission-success-modal"
    content={<SubmissionSuccessModalContent carbonProgramOutcomes={carbonProgramOutcomes} farmId={farmId} onClose={onClose} variant="modal" />}
  ></Modal>
);
